import React, { useEffect } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Box, Dialog, Typography } from '@mui/material';

/**
 * Shared success dialog, rebuilt on MUI 6 + the cardiac theme (navy/teal).
 *
 * Same contract as the previous SCSS version: shown while `isSuccessModalOpen`, auto-closes
 * itself after 2s via `handleCloseModal`. The timer now lives in an effect with cleanup —
 * previously it was started during render, so every re-render queued another close.
 */

interface SuccessProps {
  handleCloseModal?: () => void;
  title?: string;
  isSuccessModalOpen?: boolean;
}

const AUTO_CLOSE_MS = 2000;

const Success = ({ handleCloseModal, title, isSuccessModalOpen }: SuccessProps) => {
  useEffect(() => {
    if (!isSuccessModalOpen) {
      return undefined;
    }
    const timer = setTimeout(() => handleCloseModal?.(), AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessModalOpen]);

  if (!isSuccessModalOpen) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(2, 8, 15, 0.72)', backdropFilter: 'blur(3px)' },
        },
        paper: {
          sx: theme => ({
            borderRadius: 3,
            border: `1px solid ${theme.palette.cardiac.line}`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.cardiac.surface2} 0%, ${theme.palette.cardiac.surface} 100%)`,
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.55)',
          }),
        },
      }}
    >
      <Box sx={{ px: 4, py: 5, display: 'grid', justifyItems: 'center', gap: 2.5 }}>
        <Box
          sx={theme => ({
            width: 76,
            height: 76,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            color: theme.palette.cardiac.teal,
            border: `2px solid ${theme.palette.cardiac.teal}`,
            backgroundColor: 'rgba(45, 212, 191, 0.10)',
            boxShadow: '0 0 0 8px rgba(45, 212, 191, 0.06)',
          })}
        >
          <CheckRoundedIcon sx={{ fontSize: 44 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.45,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
      </Box>
    </Dialog>
  );
};

export default Success;
